import { Badge } from "@/components/ui/badge";
import { formatDateTime } from "@/lib/utils";
import type { TenantAgreementStatusDto } from "@/services/admin-tenant.service";

/** Short caption per commitment; the full name would not fit beside a second badge. */
const AGREEMENT_SHORT_LABELS: Record<string, string> = {
  signup_commitment: "Sign-up",
  vm_creation_commitment: "VM",
};

/**
 * Where a tenant or a user stands on each commitment, as one badge per commitment. Shared by the
 * tenant and user lists so both read "agreed" the same way.
 *
 * Three states, not two. `NotApplicable` means nobody has been asked — the commitment's feature
 * flag is off, or nothing has been published for it — and it is drawn as a neutral dash. Showing
 * it as "not agreed" would paint every row on the platform red before the feature is switched on
 * at all.
 */
export function AgreementStatusCell({
  statuses,
}: {
  statuses?: TenantAgreementStatusDto[];
}) {
  if (!statuses || statuses.length === 0) {
    return <span className="text-muted-foreground">—</span>;
  }

  const asked = statuses.filter((status) => status.state !== "NotApplicable");
  if (asked.length === 0) {
    return (
      <span
        className="text-muted-foreground"
        title="No commitment is being asked for yet"
      >
        —
      </span>
    );
  }

  return (
    <div className="flex flex-wrap gap-1">
      {asked.map((status) => {
        const accepted = status.state === "Accepted";
        const label = AGREEMENT_SHORT_LABELS[status.type] ?? status.type;

        // "Last signed by", not "agreed by": on a tenant each member signs for themselves, so this
        // names one person among possibly several rather than someone who signed for the workspace.
        const detail = status.acceptedAt
          ? `last signed v${status.acceptedVersion} on ${formatDateTime(status.acceptedAt)}${
              status.acceptedByEmail ? ` by ${status.acceptedByEmail}` : ""
            }`
          : "not accepted yet";

        return (
          <Badge
            key={status.type}
            variant={accepted ? "default" : "outline"}
            className={
              accepted
                ? "bg-emerald-600 text-white hover:bg-emerald-600"
                : "border-amber-500/50 text-amber-700 dark:text-amber-400"
            }
            title={`${label}: ${detail}`}
          >
            {label} {accepted ? "✓" : "✗"}
          </Badge>
        );
      })}
    </div>
  );
}
